/**
 * The bathroom staging program — one tunables table for the whole planner.
 *
 * Counts come from the staging literature, not taste. A staged bathroom is
 * essentially hotel housekeeping: the whole inventory is 6-9 props, and every
 * source is clear that the failure mode is too many, not too few ("when buyers
 * notice the staging itself, it is usually a sign the staging is not working").
 * See claude-docs/2026-09-27-open-house-staging-research.md.
 */

const { objectTypeOf } = require('../ownership');

const IN = 0.0254;

/** Hard cap on props per bathroom. The literature's range is 6-9. */
const MAX_PROPS_PER_BATHROOM = 9;

/**
 * Fittings that HANG a towel, and how many of them one bathroom may have.
 *
 * The towel basket is deliberately not one: it is the drying-zone basket of
 * rolled towels beside the tub, a different gesture from a towel hung on the
 * wall, and it is placed by a different stage.
 *
 * Two, because a staged bathroom has a hand towel by the basin and a bath
 * towel to dry off with — and that is the whole story. Adding the hooks gave
 * 13 corpus bathrooms a ring AND a bar AND a pair of hooks, which with the
 * basket is five towels in one room: "we want it to look staged, not lived in"
 * is exactly what that fails. So the hooks are an ALTERNATIVE third option
 * rather than an addition, and a room that already has two keeps them.
 */
const HANGING_TOWEL_FITTINGS = new Set([
  'staging-towel-ring',
  'staging-towel-hanging',
  'staging-towel-bath',
  'staging-towel-hooks',
]);
const MAX_HANGING_TOWEL_FITTINGS = 2;

/**
 * What to place, in priority order, with the rooms each prop belongs in.
 *
 * `roomKinds` absent means the prop is general — a tray, a candle, a plant, a
 * book stack and a framed print are as much at home on a console or a coffee
 * table as on a vanity, and those are the props the other room programmes will
 * be built from. A towel programme and a bar of soap are bathroom furniture and
 * read as a mistake anywhere else.
 *
 * `where` routes to a placer; `optional` marks a prop the budget may skip.
 */
const PROP_PROGRAM = [
  // The ring goes FIRST because it is the constrained one: it needs the
  // basin's wall specifically, while the bar will take any wall. Run the bar
  // first and on a long vanity wall it takes the far end — the ring's only
  // candidate — and the hand towel is squeezed out of the room it belongs in.
  // Same principle as the soap running before the composed row.
  //
  // It is also the DEFAULT towel, so it is not optional: "the ring should be
  // the default, the full-size rod the bonus" (2026-10-11). The two flags
  // used to say the opposite — the ring droppable and the bar guaranteed —
  // and that was invisible for as long as nothing read `optional` at all.
  {
    type: 'staging-towel-ring',
    where: 'wall',
    count: 1,
    roomKinds: ['bathroom'],
  },
  // A bath towel on a bar, anywhere a towel sensibly goes. The two coexist in
  // a real bathroom, and this is the BONUS of the pair — the first of the two
  // the cap gives up. Its rank still keeps it ahead of every other optional
  // prop, so a room loses its garnish before it loses a second towel.
  {
    type: 'staging-towel-hanging',
    where: 'wall',
    count: 1,
    optional: true,
    roomKinds: ['bathroom'],
  },
  { type: 'staging-tray', where: 'surface', count: 1 },
  { type: 'staging-plant-small', where: 'surface', count: 1 },
  { type: 'staging-candle', where: 'tray', count: 1 },
  {
    type: 'staging-soap',
    where: 'surface',
    count: 1,
    // Beside the sink, where a bar of soap actually lives.
    near: 'sink',
    roomKinds: ['bathroom'],
  },
  { type: 'staging-art', where: 'wall', count: 1 },
  { type: 'staging-jar', where: 'tray', count: 1 },
  {
    type: 'staging-bottles',
    where: 'fixture-surface',
    count: 1,
    optional: true,
    roomKinds: ['bathroom'],
  },
  {
    type: 'staging-towel-basket',
    where: 'against-wall',
    count: 1,
    optional: true,
    roomKinds: ['bathroom'],
  },
  { type: 'staging-stems', where: 'surface', count: 1, optional: true },
  // ---- The props added in phase 2 rank BELOW everything above, which all
  // ships today. A new prop earns its place out of what is left over, never by
  // evicting one that is already in people's rooms: ranked mid-programme, the
  // hooks cost 3 of the corpus's 50 towel baskets and the bottle cost 7 of its
  // 11 stems, for no reason beyond where the line happened to be typed.
  //
  // A bathroom with no roll holder reads as unfinished rather than as styled,
  // so it is the first of the three to keep when the budget is tight — but
  // OPTIONAL, and below everything that already ships.
  //
  // It was mandatory and ranked above the vignette at first, and the cap is
  // what made that wrong: eight of the nine slots were already spoken for by
  // mandatory props, so adding a ninth left exactly one for every optional
  // prop in the kit and the shower-niche bottles — which exist in 6 corpus
  // rooms and can go nowhere else — were cut in 3 of them to pay for it. A
  // roll holder is not worth a prop that only 6 bathrooms can have at all.
  {
    type: 'staging-toilet-paper',
    where: 'wall',
    count: 1,
    optional: true,
    roomKinds: ['bathroom'],
  },
  // A pair of hooks is a BONUS fitting, and the room already has a ring and a
  // bar by here. `usedWallIds` keeps it off their walls, so in practice it
  // only appears in a room with a spare wall — which is the right way for a
  // third towel fitting to be rationed.
  {
    type: 'staging-towel-hooks',
    where: 'wall',
    count: 1,
    optional: true,
    roomKinds: ['bathroom'],
  },
  // The LOTION that pairs with the bar of soap — the amenity a hotel leaves at
  // a basin alongside it. Part of the composed ROW rather than anchored to the
  // sink, which is the whole difference between it and the soap.
  //
  // It was `near: 'sink'` first, and that cost the room its vignette. An
  // anchored prop is placed BEFORE the row on purpose — one that must sit
  // somewhere specific loses every race against one that will go anywhere —
  // but a 2.4 in bottle plus its gap takes 4 in out of a run before the tray
  // is even tried, and on the corpus that was 4 trays, 7 plants and 7 stems
  // surrendered to buy 41 bottles. The soap earns that exception by being
  // mandatory; a bonus does not, and in the row its RANK decides, which is
  // where the question belongs. A bottle beside the tray also reads as the
  // group it is part of rather than as something left by the tap.
  //
  // Last of all. Greenery is the stronger staging signal of the two — the
  // florist's arrangement is what reads as styled, where a lotion bottle reads
  // as provided — so the stems take the counter's leftover space first.
  {
    type: 'staging-pump-bottle',
    where: 'surface',
    count: 1,
    optional: true,
    roomKinds: ['bathroom'],
  },
];

/**
 * The programme for a room, given its resolved kind SET. Keyed on the set and
 * not a collapsed primary kind: in an open plan a room is several kinds at
 * once, and collapsing loses that.
 */
function programFor(kinds) {
  const set = kinds instanceof Set ? kinds : new Set(kinds || []);
  return PROP_PROGRAM.filter(
    (prop) => !prop.roomKinds || prop.roomKinds.some((k) => set.has(k))
  );
}

/**
 * A tray is a VESSEL: things rest on its inner floor, not on the top of its
 * lip. This fraction of its height is the base, and the renderer draws it from
 * the same constant — fork them and props float above the tray or sink into
 * it, which is the whole reason `bedSurface.js` exists one subsystem over.
 */
const TRAY_BASE_FRACTION = 0.28;

/**
 * Host types whose usable surface is an INNER FLOOR rather than the bbox top,
 * and how far up the host's own height that floor sits.
 *
 * Deliberately NOT the same thing as "is a tray". A shower niche is a cavity
 * whose contents stand on its floor, so it belongs here; it is emphatically
 * not a tray, and the tray stage must not try to stand a candle in one. The
 * tray stage keys off `TRAY_TYPES` instead.
 */
const INNER_FLOOR_FRACTION = {
  'staging-tray': TRAY_BASE_FRACTION,
  // A niche's shelf IS its bbox bottom: `nicheRenderer` draws the shelf plane
  // at `-height / 2` and models no lip. An invented lip fraction floats the
  // contents above the drawn shelf -- 0.72 in on a 12 in niche -- which is
  // exactly the fork `TRAY_BASE_FRACTION`'s docstring warns against.
  'shower-niche': 0,
};

/**
 * Props with a FRONT — a spout, a handle, something that has to point
 * somewhere. Their renderer puts it on local +x, and the surface packer turns
 * them so that +x faces the basin.
 *
 * Everything else on a counter is radially symmetric or close enough that its
 * yaw is the host's and nobody can tell. A soap dispenser is not: its nozzle
 * pointed at the wall, which is both wrong to look at and wrong to use — you
 * pump into the sink, not over the worktop.
 */
const FRONT_FACING_PROPS = new Set(['staging-pump-bottle']);

/** Hosts the tray stage fills. Membership is what makes a thing a tray. */
const TRAY_TYPES = new Set(['staging-tray']);

/**
 * Fixtures a prop may stand ON that are not work surfaces — a shower niche.
 * Routed by `where: 'fixture-surface'`, which places ONE prop centred on the
 * host rather than packing a row along it.
 */
const FIXTURE_SURFACE_HOSTS = ['shower-niche'];

/**
 * Where a thing resting on `host` actually sits. The bbox top for a counter;
 * the inner floor for a vessel.
 */
function surfaceTopY(host) {
  const c = host?.center;
  const d = host?.dimensions;
  if (!c || !d) return null;
  const baseFraction = INNER_FLOOR_FRACTION[objectTypeOf(host)];
  if (baseFraction == null) return c.y + (d.y || 0) / 2;
  return c.y - (d.y || 0) / 2 + (d.y || 0) * baseFraction;
}

/**
 * Surfaces a prop may stand on, best first. A bathroom vanity is a
 * `lower-cabinet` carrying a `countertop` child, and the countertop is the
 * actual surface — its top is where things rest.
 */
const SURFACE_HOST_TYPES = ['countertop', 'vanity', 'lower-cabinet'];

/** Gap between props on a surface, and how close to its edge they may sit. */
const SURFACE_GAP_M = 1.5 * IN;
// Scaled to the host, not absolute: 2 in off each end of a 36 in vanity is
// right, but off each end of a 12 in tray it eats a third of the tray.
const SURFACE_EDGE_MARGIN_M = 2 * IN;
const SURFACE_EDGE_MARGIN_FRACTION = 0.06;

/**
 * Rim margin either side of a VOID in a surface — a basin, or anything else
 * that is a hole in the host rather than a thing standing on it.
 *
 * Deliberately much smaller than the edge margin, and deliberately a different
 * constant. The edge margin keeps a prop from overhanging the counter's OUTER
 * edge, where it would fall off; a basin's rim needs only enough that the prop
 * does not read as balanced on the lip. Charging the 2 in edge margin at a
 * basin boundary burns 4 in out of the middle of every counter, which is what
 * left the median vanity with 4.1 in of usable run instead of 6.1 in.
 */
const SURFACE_VOID_PAD_M = 1 * IN;

/**
 * How far along the run a `near:` prop may sit from the thing it is near.
 *
 * Without a ceiling the clamp always succeeds: a 60 in two-basin vanity
 * satisfies "beside the sink" from an outboard run 30 in away, and the soap
 * reads as belonging to nothing. 18 in is arm's reach across a counter.
 */
const MAX_ANCHOR_DISTANCE_M = 18 * IN;

/**
 * Clearance a prop keeps from each side of a recess it stands in.
 *
 * Its own constant, not the counter's rim pad: a niche is 4 in deep in this
 * corpus and 3.5 in by the gallery's default, so coupling the two means
 * retuning a counter margin silently decides which niches accept anything.
 */
const FIXTURE_CLEARANCE_M = 0.25 * IN;

/**
 * Art centre height. The museum standard is 57-60 in from the floor; 58 is
 * stated explicitly rather than borrowed, because the dorm planner's own
 * WALL_ART_EYE_HEIGHT_M is 1.55 (~61 in) and sits outside that band.
 */
const ART_CENTRE_HEIGHT_M = 58 * IN;

/** Towel bar height. 48 in AFF is the standard, and the bar is the prop's top. */
const TOWEL_BAR_HEIGHT_M = 48 * IN;

/**
 * A towel beside a vanity is a HAND towel, and a hand-towel bar is specified
 * relative to the counter -- 18-22 in above the vanity top -- not to the floor.
 *
 * At the staged 13 in drop the towel is 18 in wide against 13 in tall, i.e.
 * wider than it is tall, which IS a hand towel's proportion. Hung at the 48 in
 * bath-towel height it sits only 12.8 in above a median 35.2 in counter and
 * reads as a bath towel mounted too low. 20 in above the counter puts it at
 * ~55 in, which is where one actually goes.
 */
const HAND_TOWEL_ABOVE_COUNTER_M = 20 * IN;

/**
 * Towel RING height. The published range is 50-52 in AFF; 50 is the bottom of
 * it, and the ring is the top of the prop's box.
 */
const TOWEL_RING_HEIGHT_M = 50 * IN;

/**
 * Towel HOOK height. Hooks are mounted in a 60-70 in band and 66 in is the
 * widely-accepted figure within it. The hook is the TOP of the prop's box, as
 * the bar and the ring are.
 */
const TOWEL_HOOK_HEIGHT_M = 66 * IN;

/**
 * Toilet roll holder height — the NKBA's 26 in above the finished floor, to
 * the VERTICAL CENTRE of the dispenser. Measured from the centre, unlike every
 * towel fitting here, because that is what the published figure describes.
 * ADA puts the centreline between 19 and 44 in, so 26 sits inside it.
 */
const TOILET_PAPER_HEIGHT_M = 26 * IN;

/**
 * Clear space the holder keeps between itself and the toilet, along the wall.
 *
 * NOT the published 8-12 in (ADA 7-9). That figure is measured FORWARD of the
 * bowl's front edge — it describes a holder on the side wall of a toilet
 * alcove, on the axis running out of the wall the pan backs onto. This gap is
 * on the other axis, along that wall, where the only real requirement is that
 * the holder is beside the pan rather than over it.
 *
 * Applying the 9 in here was an over-reading of the source and it cost the
 * prop almost every room: with the 4 in wall-end margin and the holder's own
 * half-width counted twice over, it demanded 19 in of clear wall past the
 * toilet's edge, and the holder placed in 5 of the 55 corpus bathrooms that
 * have a toilet at all. 2 in is a hand's clearance; `TP_NEAR_TOILET_M` is what
 * keeps it close.
 */
const TP_BESIDE_TOILET_M = 2 * IN;

/**
 * How far from the toilet a holder may end up before it stops being that
 * toilet's. Same standard, and for the same reason, as the towel ring's reach
 * to its basin: the slide target is clamped into the wall's clear run, so a
 * wall that qualifies can still put the holder across the room. 18 in is the
 * reach of someone sitting down.
 */
const TP_NEAR_TOILET_M = 18 * IN;

/**
 * A toilet roll holder must not hang over these.
 *
 * The toilet is in the set even though the holder REQUIRES the toilet's wall,
 * and the two are not in tension: the wall is a different claim from the
 * position. Over the toilet means over the cistern, out of reach. A counter is
 * there because 26 in is INSIDE a 34.5 in vanity — the holder would be behind
 * the cabinet door.
 */
const TP_REFUSE_OVER = new Set([
  'toilet',
  'countertop',
  'vanity',
  'lower-cabinet',
]);

/**
 * How far from the basin a towel ring may end up before it stops being that
 * basin's towel ring.
 *
 * The slide target is CLAMPED into the wall's clear run, so when the run is
 * far from the sink the ring lands wherever the run is and still counts as
 * "on the vanity wall" — 88 in away in the worst corpus room. Arm's reach
 * along a counter is the test, and a ring that cannot meet it is not placed.
 */
const RING_NEAR_BASIN_M = 36 * IN;

/** How close to a counter a towel has to be to count as that counter's. */
const TOWEL_NEAR_COUNTER_M = 36 * IN;

/**
 * Ceiling on a counter-derived bar height. A few scanned "counters" measure
 * 41-45 in, which would put the bar at 65+ in -- above comfortable reach for
 * something you grab with wet hands. The counter rule is a relationship, not a
 * licence to follow bad data upward.
 *
 * 60 rather than 56 because the hem rule needs the headroom: a ring's 18 in
 * drape over a 37 in counter wants its ring at 58 in, and clamping it to 56
 * put the hem back on the counter -- the cap silently defeating the clearance
 * it was meant to coexist with. A candidate that still cannot clear is now
 * REFUSED rather than clamped.
 */
const MAX_TOWEL_BAR_HEIGHT_M = 60 * IN;

/**
 * How much of a wall a tub or shower claims around itself.
 *
 * A plan-footprint overlap is not enough: a tub whose rim stops a few inches
 * short of the wall leaves a gap an inch-thick towel slips through, so the
 * towel reads as hanging over the tub while the test says it does not. One
 * corpus bathroom had the bar 5 in from the tub edge and "not overlapping".
 * A wet fixture blocks the wall SECTION it stands in front of.
 */
const WET_WALL_CLEARANCE_M = 18 * IN;

/**
 * Air a hanging towel keeps between its hem and a surface below it.
 *
 * The mount rules put the BAR at a height; the drape then hangs the box's
 * whole height below it, and nothing checked where the hem landed. A ring at
 * counter + 20 in with an 18 in drape clears a 37 in counter by 0.7 in, which
 * reads as a towel lying on the worktop.
 */
const DRAPE_CLEARANCE_M = 3 * IN;

/** Art runs about two-thirds the width of the furniture under it. */
const ART_WIDTH_FRACTION_OF_HOST = 0.66;

/**
 * How close to a wall's plane an object has to be to count as standing
 * AGAINST that wall — i.e. for a piece hung on it to be hanging over the
 * object.
 *
 * Measured. Of the corpus cases where a fixture overlaps a hung piece along
 * the wall AND the piece's hem finishes below the fixture's top, the ones
 * where the towel visually passes THROUGH the fixture are all within ~3 in of
 * the wall (a scanned vanity sits an inch or two off the face). At 6 in all
 * of those are refused and none remain.
 *
 * The next two conflicts sit at 6.5 in and 15.3 in, and are deliberately out:
 * an object standing that far into the room has the towel hanging BEHIND it,
 * not through it, which is just a fixture in front of a towel rail. After
 * those the nearest is 32 in — across the room, and nothing to do with this
 * wall.
 *
 * This is deliberately NOT the nearest-wall attribution. A vanity in a corner
 * is ~0 in from BOTH its walls and a towel on either of them hangs in front
 * of it, so both must refuse; attribution picks one and lets the other
 * through.
 */
const AGAINST_WALL_M = 6 * IN;

/**
 * A towel should not hang directly above these. The toilet is the thing
 * staging guides play DOWN; the vanity would put the towel in front of the
 * mirror and over the counter props. A demotion, not a ban.
 */
/**
 * Wet zones are NOT in this set. They are FORBIDDEN outright for a towel, not
 * demoted: a towel over the water gets wet and can only be reached by leaning
 * across the tub, and "an awkward towel beats no towel" was the wrong trade --
 * demotion left 5 of 69 corpus towels hanging over a tub in rooms with no
 * other wall. A bathroom with no towel is the better answer.
 */
const AVOID_HANGING_OVER = new Set([
  'toilet',
  'countertop',
  'vanity',
  'lower-cabinet',
]);

/**
 * The drying zone. Designers put a towel basket beside the tub or shower, or
 * failing that by the vanity — a wicker basket just has to stay out of the
 * spray itself, which standing against a wall takes care of.
 */
const WET_ZONE_TYPES = new Set([
  'bathtub',
  'tub',
  'tub-shower',
  'shower',
  'shower-door',
]);

/**
 * Everything that says "this room has a bath or a shower to dry off from" —
 * the wet-zone footprints above AND the parts a shower or a tub is drawn
 * from: a scanned room often carries a showerhead and controls with no
 * `shower` footprint at all, a tub as its surface, support box and faucet.
 * The body-towel gate reads THIS set, not the wet zone: "if we render a
 * shower in the room, regardless of whether or not roomplan detected it, we
 * should try to place the body towel" (2026-10-17). The wet-zone set stays
 * what it is — clearance is kept from a footprint, not from a showerhead.
 */
const BATHING_FIXTURE_TYPES = new Set([
  ...WET_ZONE_TYPES,
  'showerhead',
  'shower-controls',
  'shower-niche',
  'shower-stem',
  'shower-pan',
  'tub-surface',
  'tub-support-box',
  'tub-faucet',
  'bathroom-tub-faucet',
]);

/**
 * Where a bathroom's art wants to be. Above the toilet is one of the two spots
 * the literature names, and it is usually the only dead wall in the room.
 */
const ART_PREFERS_OVER = new Set(['toilet']);

/**
 * Gap between the top of whatever art hangs over and the bottom of its frame.
 * Designers use 6-8 in, and that relationship outranks the 57-60 in centre
 * line whenever art hangs above something — which is the whole reason art is
 * allowed to share the towel's wall.
 */
const ART_STACK_GAP_M = 6 * IN;

/**
 * How BAD each demotion is, when a towel has nowhere ideal to go.
 *
 * Every wall being demoted is the common case in a small bathroom, and the
 * first version then took the FIRST demoted candidate — which, because
 * candidates are ranked by free run, meant the WIDEST wall rather than the
 * least objectionable one. In one corpus bathroom that put the towel over the
 * tub on a 62 in wall while 36 in of clear wall sat beside the vanity.
 *
 * Over a counter is barely a demotion at all: that is a HAND towel by the
 * sink, which is what the counter-relative mount height exists for. Over a
 * toilet is awkward. Over a tub or shower means it gets wet and can only be
 * reached across the water, so it is the last resort.
 */
const AVOID_SEVERITY = {
  countertop: 1,
  vanity: 1,
  'lower-cabinet': 1,
  toilet: 2,
  bathtub: 3,
  tub: 3,
  'tub-shower': 3,
  shower: 3,
};

/**
 * A door needs its OWN WIDTH of clear floor to swing into.
 *
 * Skipping the door's WALL is not enough: a basket against the ADJACENT wall,
 * just round the corner from the opening, sits squarely in the arc and gets
 * knocked every time the door opens.
 *
 * Scans record no hinge side, so the swept quarter-circle is unknowable — but
 * whichever way it hinges, the leaf stays within the door's own width of the
 * opening, both along the wall and into the room. So the keep-out is that
 * RECTANGLE, not a circle. A semicircle is the union of both possible arcs and
 * is 57% larger; tried first, it cost 14 of 61 corpus baskets their only spot,
 * which is a worse answer than the bug it fixed.
 */
const DOOR_SWING_MIN_M = 28 * IN;

/**
 * How far a floor prop may end up from the fixture it belongs to.
 *
 * The basket's whole rule is "the drying zone beside the tub or shower, or by
 * the vanity within reach of the sink". When neither wall has clear floor the
 * search falls through to whatever wall does, and 10 of 48 corpus baskets
 * ended up 30-50 in from BOTH -- standing against a wall in the middle of
 * nowhere, which is the "something somebody dropped" the rule exists to
 * prevent. 36 in is a stride; past that it is not that fixture's basket.
 */
const FLOOR_PROP_NEAR_ANCHOR_M = 36 * IN;

/** A wall needs this much clear run to take a piece, plus the piece's width. */
const WALL_END_MARGIN_M = 4 * IN;

/** A floor prop stands this far off the wall face, and needs this much clear floor. */
const FLOOR_WALL_GAP_M = 0.02;
const FLOOR_CLEARANCE_M = 0.12;
/** How finely a wall is sampled looking for a clear spot for a floor prop. */
const FLOOR_SAMPLE_STEP_M = 0.1;

/**
 * Which staged props HANG, and at what nominal height to the centre.
 *
 * The planner computes a real height per room (a bar lifts over a counter, art
 * stacks above a bar), so these are the NOMINAL values — but they are also
 * what `objectTypeConfigs` hands the manual-placement path, and the two must
 * not drift. A prop missing here is a surface prop, which mounts as 'counter'
 * and keeps whatever Y it was seated at.
 *
 * Which of them a height describes — the prop's TOP or its CENTRE — is
 * `STAGING_WALL_MOUNT_MEASURE_FROM`, below, read by both consumers.
 */
const STAGING_WALL_MOUNT_HEIGHTS = {
  'staging-towel-hanging': TOWEL_BAR_HEIGHT_M,
  'staging-towel-bath': TOWEL_BAR_HEIGHT_M,
  'staging-towel-ring': TOWEL_RING_HEIGHT_M,
  'staging-towel-hooks': TOWEL_HOOK_HEIGHT_M,
  'staging-toilet-paper': TOILET_PAPER_HEIGHT_M,
  'staging-art': ART_CENTRE_HEIGHT_M,
};

/**
 * What each height above DESCRIBES: the prop's TOP (the hardware line of a
 * bar, a ring or a hook — the towel hangs below it) or its CENTRE (a holder,
 * a print). One table, read by the planner's `hangOnWall` options AND by the
 * manual-placement config (`objectTypeConfigs` → `getVerticalPosition`), so
 * the two seat the same object at the same Y. Until 2026-10-17 they did not:
 * the planner hung a bath towel with its top at 48 in and the viewer's drag
 * re-seated its CENTRE there, 13 in higher — five of the six entries
 * disagreed, and the difference was "recorded rather than changed" here.
 */
const STAGING_WALL_MOUNT_MEASURE_FROM = {
  'staging-towel-hanging': 'top',
  'staging-towel-bath': 'top',
  'staging-towel-ring': 'top',
  'staging-towel-hooks': 'top',
  'staging-toilet-paper': 'centre',
  'staging-art': 'centre',
};

module.exports = {
  IN,
  MAX_PROPS_PER_BATHROOM,
  HANGING_TOWEL_FITTINGS,
  MAX_HANGING_TOWEL_FITTINGS,
  PROP_PROGRAM,
  programFor,
  SURFACE_HOST_TYPES,
  INNER_FLOOR_FRACTION,
  TRAY_TYPES,
  FRONT_FACING_PROPS,
  FIXTURE_SURFACE_HOSTS,
  TRAY_BASE_FRACTION,
  surfaceTopY,
  SURFACE_GAP_M,
  SURFACE_EDGE_MARGIN_M,
  SURFACE_EDGE_MARGIN_FRACTION,
  SURFACE_VOID_PAD_M,
  MAX_ANCHOR_DISTANCE_M,
  FIXTURE_CLEARANCE_M,
  ART_CENTRE_HEIGHT_M,
  STAGING_WALL_MOUNT_HEIGHTS,
  STAGING_WALL_MOUNT_MEASURE_FROM,
  TOWEL_BAR_HEIGHT_M,
  HAND_TOWEL_ABOVE_COUNTER_M,
  TOWEL_RING_HEIGHT_M,
  TOWEL_HOOK_HEIGHT_M,
  TOILET_PAPER_HEIGHT_M,
  TP_BESIDE_TOILET_M,
  TP_NEAR_TOILET_M,
  TP_REFUSE_OVER,
  RING_NEAR_BASIN_M,
  TOWEL_NEAR_COUNTER_M,
  MAX_TOWEL_BAR_HEIGHT_M,
  AGAINST_WALL_M,
  WET_WALL_CLEARANCE_M,
  DRAPE_CLEARANCE_M,
  ART_WIDTH_FRACTION_OF_HOST,
  WALL_END_MARGIN_M,
  AVOID_HANGING_OVER,
  AVOID_SEVERITY,
  WET_ZONE_TYPES,
  BATHING_FIXTURE_TYPES,
  ART_PREFERS_OVER,
  ART_STACK_GAP_M,
  FLOOR_WALL_GAP_M,
  FLOOR_CLEARANCE_M,
  DOOR_SWING_MIN_M,
  FLOOR_PROP_NEAR_ANCHOR_M,
  FLOOR_SAMPLE_STEP_M,
};
